import type { Tokens } from "@okta/okta-auth-js";

/**
 * Shared contract for the Storyblok editor "popup login handoff".
 *
 * Okta's login page refuses to render inside the Storyblok editor's third-party iframe, so the
 * editor opens a top-level popup that completes the normal Okta login and posts the resulting
 * tokens back to the iframe.
 *
 * The handoff marker + per-attempt nonce are carried through Okta's `state` parameter, which
 * round-trips in the callback URL (`/login/callback?state=...`). This is deliberately NOT in
 * `sessionStorage`: the popup must reliably be recognised as a handoff on return so it relays
 * and closes, rather than falling through to the normal login callback and navigating to a
 * guarded app route.
 */

/** Prefix marking an Okta `state` value as a preview-login handoff; the nonce follows it. */
export const PREVIEW_HANDOFF_STATE_PREFIX = "pulse-preview-";

/** `postMessage` type used to hand tokens from the popup back to the editor iframe. */
export const PREVIEW_HANDOFF_MESSAGE_TYPE = "pulse-preview-login-handoff";

export interface PreviewHandoffMessage {
  type: typeof PREVIEW_HANDOFF_MESSAGE_TYPE;
  /** Random per-attempt value; the iframe rejects messages whose nonce it didn't issue. */
  nonce: string;
  tokens: Tokens;
}

/** Build the Okta `state` value for a handoff carrying the given nonce. */
export const buildPreviewHandoffState = (nonce: string): string =>
  `${PREVIEW_HANDOFF_STATE_PREFIX}${nonce}`;

/** The handoff nonce from the current callback URL's `state` param, or `null` if not a handoff. */
const handoffNonceFromUrl = (): string | null => {
  const state = new URLSearchParams(window.location.search).get("state");
  return state?.startsWith(PREVIEW_HANDOFF_STATE_PREFIX)
    ? state.slice(PREVIEW_HANDOFF_STATE_PREFIX.length)
    : null;
};

/** Whether the current `/login/callback` load is a preview popup handoff (not a normal login). */
export const isPreviewLoginHandoff = (): boolean => handoffNonceFromUrl() !== null;

/** The nonce issued for this handoff (empty string if the current load is not a handoff). */
export const readPreviewHandoffNonce = (): string => handoffNonceFromUrl() ?? "";
